import { useContext, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import * as api from '@/apis/aipass'
import { LoaderContextCreator } from '@/contexts/loader'
import { useSelfCheckOutState } from '@/hooks/use-self-check-out-state'
import { reloadIfVersionHasChanged } from '@/utils/force-version-up'
import { signout } from '@/apis/auth0'
import { PaymentIndex, PaymentMethodType, PaymentTerminalSettingType, TerminalPaymentScene } from '@/models/payment-setting'
import { reportError } from '@/components/apps/error-boundary'

type InitializationOptions = {
  onInitialized?: () => void | Promise<void>
}

/**
 * セルフチェックアウト画面の初期化処理を共通化したカスタムフック
 * hotelIdの検証、施設情報・決済設定の取得、状態の初期化を行う
 */
export const useSelfCheckoutInitialization = (options?: InitializationOptions) => {
  const { setIsLoading } = useContext(LoaderContextCreator())
  const history = useHistory()
  const locateInfo = useLocation<{ hotelId: string }>()
  const { state, initializeState } = useSelfCheckOutState()
  const [isInitialized, setIsInitialized] = useState(false)
  const [initializedState, setInitializedState] = useState<ReturnType<typeof initializeState> | null>(null)

  const fetchPaymentSetting = async (
    hotelId: string,
  ): Promise<{
    terminal: PaymentTerminalSettingType
    manual: Array<PaymentMethodType>
  }> => {
    return api.fetchPaymentSettingByHotelId(hotelId).then(res => {
      const terminal: PaymentTerminalSettingType = {
        isEnabledCash: res?.terminals?.isEnabledCash ?? true,
        isEnabledQr: res?.terminals?.isEnabledQr ?? false,
        terminalPaymentScene: res?.terminals?.terminalPaymentScene ?? TerminalPaymentScene.Both,
      }

      const tabletCheckoutPaymentSetting = res?.settings?.payment_time?.find(item => item.value === PaymentIndex.SelfCheckout)
      if (!tabletCheckoutPaymentSetting?.is_enabled) {
        return { terminal, manual: [] }
      }
      const temp: PaymentMethodType[] = []
      for (let index = 0; index < tabletCheckoutPaymentSetting.payment_method.length; index++) {
        const paymentMethod = tabletCheckoutPaymentSetting.payment_method[index]
        if (paymentMethod.is_enabled) {
          temp.push(paymentMethod.value)
        }
      }
      return { terminal, manual: temp }
    })
  }

  const initializeHotelInfo = async (hotelId: string) => {
    const [{ results }, paymentSetting] = await Promise.all([
      api.fetchFacilityBasicInfoInSelfCheckin(hotelId),
      fetchPaymentSetting(hotelId),
    ])
    const _initializedState = initializeState(hotelId, {
      basicInfo: results,
      payment: paymentSetting.manual,
      paymentTerminal: paymentSetting.terminal,
    })
    setInitializedState(_initializedState)
  }

  useEffect(() => {
    window.scrollTo(0, 0)
    if (reloadIfVersionHasChanged()) {
      return
    }
    if (!state?.hotelId) {
      const locateHotelId = locateInfo.state?.hotelId || sessionStorage.getItem('selfci.hotelId')
      if (locateHotelId) {
        history.replace({ pathname: '/self-checkin', search: `?hotelId=${locateHotelId}` })
        return
      }
      signout()
      history.push({ pathname: '/dashboard' })
      return
    }

    setIsLoading(true)
    initializeHotelInfo(state.hotelId)
      .then(async () => {
        if (options?.onInitialized) {
          await options.onInitialized()
        }
        setIsInitialized(true)
      })
      .catch(e => reportError(e))
      .finally(() => setIsLoading(false))
  }, [])

  return { isInitialized, initializedState }
}
